import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { MarketoForm } from '@gonfalon/marketing-forms/forms';
import { isUnifiedFormSubmitEnabled } from '@gonfalon/marketing-forms/isUnifiedFormSubmitEnabled';
import { useMarketingFormSubmit } from '@gonfalon/marketing-forms/useMarketingFormSubmit';
import { trackContactSalesFormSubmitted } from '@gonfalon/marketo/analytics';
import { msg } from '@gonfalon/messages/msg';
import { checkAccessFromDenials } from '@gonfalon/permissions/access';
import { toastQueue } from '@launchpad-ui/components';

import { ContactUsBackend } from './internal/ContactUsBackend';
import { ContactUsDialog } from './internal/ContactUsDialog';
import type { ContactUsFormValues, ContactUsProps } from './internal/types';

function ContactUsUnified({ plan, cta, source, ...props }: ContactUsProps) {
  const { account } = useAccountContext();
  // The member endpoint requires the same subscription-management access the
  // billing surfaces do, but contact sales also renders where any member can
  // reach it — the trial banner, AI Configs, guarded rollouts. Members without
  // that access would get a 403, so they submit as visitors instead: no account
  // permissions, at the cost of a captcha.
  const canManageSubscription = checkAccessFromDenials(account._access, 'updateSubscription').isAllowed;

  const { submit, isPending, captcha } = useMarketingFormSubmit({
    formId: MarketoForm.ContactSales,
    audience: canManageSubscription ? 'member' : 'visitor',
  });

  const handleSubmit = (
    { email, company, firstName, lastName, jobTitle, moreDetails }: ContactUsFormValues,
    close: () => void,
  ) => {
    submit(
      // Keys must match the marketo-form-submission-config allow-list for this
      // form ID, which uses Marketo's own lead field names.
      {
        email,
        firstName,
        lastName,
        company,
        title: jobTitle,
        Description__c: moreDetails,
        cta_source: source,
      },
      {
        onSuccess: () => {
          trackContactSalesFormSubmitted({
            planType: plan?.planType,
            component: source,
            cta,
            ...(moreDetails ? { description: moreDetails } : {}),
          });
          toastQueue.add({ title: msg('billing.contactSales.successToast'), status: 'success' });
          close();
        },
        onError: () => {
          toastQueue.add({ title: msg('billing.contactSales.errorToast'), status: 'error' });
        },
      },
    );
  };

  return (
    <ContactUsDialog
      plan={plan}
      cta={cta}
      source={source}
      {...props}
      isPending={isPending}
      captcha={captcha}
      onSubmit={handleSubmit}
    />
  );
}

// An unreleased form ID falls back to the bespoke endpoint rather than to the
// Marketo client script: this surface has never embedded it. The contact-us
// package still does on its own flag-off path (see ContactSalesFormLegacy).
export const ContactUs = (props: ContactUsProps) =>
  isUnifiedFormSubmitEnabled(MarketoForm.ContactSales) ? (
    <ContactUsUnified {...props} />
  ) : (
    <ContactUsBackend {...props} />
  );
